import { atom, useAtom } from 'jotai';
import { overlay } from 'overlay-kit';
import { useCallback } from 'react';
import { Outlet } from 'react-router-dom';

import Portal from '@/components/Portal/Portal';
import { Stage } from '@/components/Stage/Stage';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import {
  CreateSignalAlertDialog,
  SignalAlertHistoryDialog,
  SignalAlertListPane,
  UpdateSignalAlertDialog,
} from '@/features/(signal)/signal-alert';
import {
  DEFAULT_SIGNAL_DATE_SELECTION,
  signalDatePresetToDurationDays,
} from '@/features/(signal)/signals/lib/signal-date-preset.utils';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import { createStorageParser } from '@/utils/storage';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import {
  type SignalDateSelection,
  storedSignalDateSelectionSchema,
} from '../../model';
import { HiddenIssueListPane } from '../hidden-issue-list';

type SignalLayoutPane = 'alert' | 'hiddenIssues';

export const SignalLayoutPaneAtom = atom<SignalLayoutPane | null>(null);

export type AlertModalState = {
  openCreate: () => void;
  openEdit: (alert: SignalAlertWorkflow) => void;
  openHistory: (alertIds: string[]) => void;
};

export const useSignalLayout = () => {
  const [signalLayout, setSignalLayout] = useAtom(SignalLayoutPaneAtom);
  const [dateSelection] = useWorkspaceStorage<SignalDateSelection>(
    'signals-date-preset',
    DEFAULT_SIGNAL_DATE_SELECTION,
    {
      parser: createStorageParser(
        storedSignalDateSelectionSchema,
        DEFAULT_SIGNAL_DATE_SELECTION,
      ),
    },
  );

  const openAlertPane = useCallback(() => {
    setSignalLayout('alert');
  }, [setSignalLayout]);

  const openHiddenIssuesPane = useCallback(() => {
    setSignalLayout('hiddenIssues');
  }, [setSignalLayout]);

  const closePane = useCallback(() => {
    setSignalLayout(null);
  }, [setSignalLayout]);

  const alertModalState: AlertModalState = {
    openCreate: () => {
      closePane();
      const workspaceId = getCurrWorkspaceAlias();
      overlay.open(({ isOpen, close, unmount }) => (
        <CreateSignalAlertDialog
          isOpen={isOpen}
          close={close}
          unmount={unmount}
          workspaceId={workspaceId}
          defaultDuration={signalDatePresetToDurationDays(dateSelection)}
          onViewList={openAlertPane}
        />
      ));
    },
    openEdit: (alert: SignalAlertWorkflow) => {
      closePane();
      const workspaceId = getCurrWorkspaceAlias();
      overlay.open(({ isOpen, close, unmount }) => (
        <UpdateSignalAlertDialog
          isOpen={isOpen}
          close={close}
          unmount={unmount}
          alert={alert}
          workspaceId={workspaceId}
        />
      ));
    },
    openHistory: (alertIds: string[]) => {
      closePane();
      overlay.open(({ isOpen, close, unmount }) => (
        <SignalAlertHistoryDialog
          isOpen={isOpen}
          close={close}
          unmount={unmount}
          alertIds={alertIds}
        />
      ));
    },
  };

  return {
    signalLayout,
    openAlertPane,
    openHiddenIssuesPane,
    closePane,
    alertModalState,
  };
};

export const SignalsLayout = () => {
  const { signalLayout, closePane, alertModalState } = useSignalLayout();

  return (
    <div>
      <Outlet />
      <Portal id="detail-modal-layer">
        <ToggleStage
          status={signalLayout !== null}
          options={{
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0 },
            transition: { duration: 0.2 },
          }}
        >
          <div
            className="fixed top-0 left-0 w-full h-full bg-black opacity-50 z-[9999]"
            onClick={closePane}
          />
        </ToggleStage>
        <ToggleStage
          status={signalLayout !== null}
          options={{
            initial: { translateX: '50%', opacity: 0 },
            animate: { translateX: '0%', opacity: 1 },
            exit: { translateX: '50%', opacity: 0 },
            transition: { duration: 0.3 },
          }}
          className="fixed top-0 right-0 h-screen w-120 bg-gray-100 z-[10000]"
        >
          <Stage>
            <Stage.Scene status={signalLayout === 'alert'}>
              <SignalAlertListPane
                onClose={closePane}
                alertModalState={alertModalState}
              />
            </Stage.Scene>
            <Stage.Scene status={signalLayout === 'hiddenIssues'}>
              <HiddenIssueListPane onClose={closePane} />
            </Stage.Scene>
          </Stage>
        </ToggleStage>
      </Portal>
    </div>
  );
};
